import { t } from 'i18next';
import IUserManager, { IQueryCustomerInfoRequestProps, IValidateOtp } from './IUserManager';
import Logger from '../../Utils/Logger';
import IUserService, { IDocument, IKycDetails } from '../../Service/IUserService';
import IDataManager from '../IDataManager';
import DI from '../../Utils/DI';
import ResourceAvailabilityInformation from '../data/ResourceAvailabilityInformation';
import {
    BLOCKED_IP_INTERVAL_IN_SECONDS, LOGGER_TAGS, OTP_OPERATION_TYPE, RESEND_CODE_INTERVAL_IN_SECONDS
} from '../../Utils/Constants';
import { ERROR_CODES } from '../../Utils/Constants/Errors';
import { UserProps } from '../data/User';
import SpamGuardManager from '../SpamGuardManager';
import IResourceAvailabilityInformation from '../IResourceAvailabilityInformation';
import { InternalResourceStatusEnum } from '../../Protocol/IHttpInterface';
import Utils from '../../Utils/Utils';
import ISessionManager from '../ISessionManager';
import useStepContext from '../../UI/Hooks/useStepContext';

class UserManager implements IUserManager, IResourceAvailabilityInformation {
    protected msisdnInformation!: ResourceAvailabilityInformation;

    protected _msisdn!: string;

    private static _instance: UserManager;

    private _isWhitelistedUser: boolean = false;

    private _premiumExists: boolean = false;

    private _customerExists: boolean = false;

    private _identityDocuments: IDocument[] = [];

    private _kycDetails?: IKycDetails;

    private operationType!: OTP_OPERATION_TYPE;

    private _userInformation!: UserProps;

    protected _hasFlowStarted: boolean = false;

    private _isValidStatus: boolean = false;

    constructor() {
        Logger.log(
            LOGGER_TAGS.USER_MANAGER,
            `${LOGGER_TAGS.USER_MANAGER} created.`
        );
    }

    protected get userService(): IUserService {
        return DI.get('UserService');
    }

    protected get dataManager(): IDataManager {
        return DI.get('DataManager');
    }

    protected get sessionManager(): ISessionManager {
        return DI.get('SessionManager');
    }

    public static getInstance(): UserManager {
        if (!UserManager._instance) {
            UserManager._instance = new UserManager();
        }
        return UserManager._instance;
    }

    public setWhitelistedUser(flag: boolean): void {
        this._isWhitelistedUser = flag;
        Logger.log(LOGGER_TAGS.USER_MANAGER, `Whitelisted user set to: ${flag}`);
    }

    public isWhitelistedUser(): boolean {
        return this._isWhitelistedUser;
    }

    public setIdentityDocuments(documents: IDocument[]) {
        this._identityDocuments = documents;
    }

    public getIdentityDocuments(): IDocument[] {
        return this._identityDocuments;
    }

    public setKycDetails(details: IKycDetails) {
        this._kycDetails = details;
    }

    public getKycDetails(): IKycDetails | undefined {
        return this._kycDetails;
    }

    public setPremiumUser(flag: boolean): void {
        this._premiumExists = flag;
    }

    public setValidStetus(flag: boolean): void {
        this._isValidStatus = flag;
    }

    public setOldCustomerExists(flag: boolean): void {
        this._customerExists = flag;
    }

    requestMsisdnValidation(msisdn: string): Promise<ResourceAvailabilityInformation> {
        throw new Error('Method not implemented.');
    }

    requestMsisdnValidationFromEntitledManager(msisdn: string): Promise<any> {
        const spamGuardManager = new SpamGuardManager();
        return new Promise((resolve, reject) => {
            spamGuardManager.performSpamSafetyGuard()
                .then(async () => {
                    const entitledManager = this.dataManager.convertToUserManager();
                    try {
                        const response = await entitledManager.requestMsisdnValidation(msisdn);
                        const parentUserManager: IUserManager = DI.get('UserManager');
                        parentUserManager.setMsisdn(this._msisdn);
                        parentUserManager.setMsisdnInformation(response);
                        resolve(response);
                    } catch (error) {
                        Logger.error(LOGGER_TAGS.USER_MANAGER, 'Failed to validate msisdn', error);
                        reject(error);
                    }
                })
                .catch((error) => reject(error));
        });
    }

    validateOtp(validateOtpRequest: IValidateOtp): Promise<any> {
        throw new Error('Method not implemented.');
    }

    sendOtp(): Promise<ResourceAvailabilityInformation> {
        throw new Error('Method not implemented.');
    }

    queryCustomerInfo(
        { msisdn, relativeUrl }: IQueryCustomerInfoRequestProps,
        setWhitelistedUserCallback?: (flag: boolean) => void
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            this.userService.queryCustomerInfo({
                relativeUrl,
                data: { msisdn }
            }).then(
                (response) => {
                    const customerInfo = response;
                    if (response?.status === InternalResourceStatusEnum.BLOCKED) {
                        this.msisdnInformation = this.dataManager.convertToMsisdnInformation({
                            status: InternalResourceStatusEnum.BLOCKED,
                            timeLeft: response?.timeLeft || RESEND_CODE_INTERVAL_IN_SECONDS,
                            liteExists: response?.timeLeft || false
                        });
                        return reject({
                            errorCode: ERROR_CODES.VALIDATE_MSISDN.BLOCKED,
                            manager: this
                        });
                    }

                    if (response?.exists) {
                        // this._customerExists = true;
                        this.setPremiumExists(!!customerInfo?.premium);
                        this.setWhiteListedMsisdn(!!customerInfo?.whiteListedMsisdn);
                        this.setCustomerExists(!!customerInfo?.exists);
                        this.setValidStatus(!!customerInfo?.validStatus);
                        if (setWhitelistedUserCallback) {
                            setWhitelistedUserCallback(this.getWhiteListedMsisdn());
                        }

                        if (!customerInfo?.premium && customerInfo?.kycDetails) {
                            this.setKycDetails(customerInfo.kycDetails);
                        }

                        this.msisdnInformation = this.dataManager.convertToMsisdnInformation({
                            status: InternalResourceStatusEnum.SUCCESS,
                            timeLeft: response?.timeLeft || RESEND_CODE_INTERVAL_IN_SECONDS,
                            liteExists: response?.liteExists || false
                        });

                        return resolve({
                            exists: !!customerInfo?.exists,
                            premium: !!customerInfo?.premium,
                            whiteListedMsisdn: !!customerInfo?.whiteListedMsisdn,
                            validStatus: !!customerInfo.validStatus,
                            kycDetails: customerInfo?.kycDetails,
                            msisdnInformation: this.msisdnInformation
                        });
                    }

                    // new customer branch
                    this._msisdn = msisdn;
                    this.setWhiteListedMsisdn(!!response?.whiteListedMsisdn);
                    this.setValidStatus(!!response?.validStatus);

                    return resolve({
                        exists: !!response.exists,
                        premium: !!response.premium,
                        whiteListedMsisdn: !!response?.whiteListedMsisdn,
                        validStatus: !!response?.validStatus,
                        msisdnInformation: this.msisdnInformation
                    });
                },
                (error) => {
                    const errorCode = Utils.convertToErrorCode(error?.errorCode);
                    if (errorCode === ERROR_CODES.REQUEST_OTP.IP_BLOCKED) {
                        this.msisdnInformation = this.dataManager.convertToMsisdnInformation({
                            status: InternalResourceStatusEnum.BLOCKED,
                            timeLeft: error?.additionalFields?.remainingTime / 1000 || BLOCKED_IP_INTERVAL_IN_SECONDS
                        });
                        this._msisdn = msisdn;
                    }

                    reject({
                        errorCode: errorCode || ERROR_CODES.GENERIC_ERROR,
                        manager: this
                    });
                }
            );
        });
    }

    getMsisdnInformation(): ResourceAvailabilityInformation {
        return this.msisdnInformation;
    }

    setMsisdnInformation(value: ResourceAvailabilityInformation): void {
        this.msisdnInformation = value;
    }

    getMsisdn(): string {
        return this._msisdn;
    }

    setMsisdn(value: string) {
        this._msisdn = value;
    }

    getWhiteListedMsisdn(): boolean {
        return this._isWhitelistedUser;
    }

    setWhiteListedMsisdn(value: boolean): void {
        this._isWhitelistedUser = value;
    }

    setValidStatus(flag: boolean) {
        this._isValidStatus = flag;
    }

    getValidStatus(): boolean {
        return this._isValidStatus;
    }

    setPremiumExists(flag: boolean) {
        this._premiumExists = flag;
    }

    getPremiumExists(): boolean {
        return this._premiumExists;
    }

    getOperationType(): OTP_OPERATION_TYPE {
        return this.operationType;
    }

    setOperationType(value: OTP_OPERATION_TYPE) {
        this.operationType = value;
    }

    public getUserInformation(): UserProps {
        return this._userInformation;
    }

    public saveUserInformation(value: UserProps) {
        this._userInformation = value;
    }

    public setCustomerExists(flag: boolean): void {
        this._customerExists = flag;
        Logger.log(LOGGER_TAGS.USER_MANAGER, `Customer exists set to: ${flag}`);
    }

    public getCustomerExists(): boolean {
        return this._customerExists;
    }

    retrieveResourceAvailabilityInformation(): ResourceAvailabilityInformation {
        return this.getMsisdnInformation();
    }
}

export default UserManager;
